/*
 * EaseFlow's product page (sections/flow-*.liquid):
 *
 *   [data-flow-replay]      a scene that plays again on hover or tap, once
 *                           its entrance has run (motion-scenes.js)
 *   [data-flow-auto]        Auto Mode in Effect Controls, played three times
 *   [data-flow-playground]  the graph, the motion strip and the preset tiles
 *   [data-flow-choose]      a link that picks a version in the buy box
 *
 * The easing maths is EaseFlow's own (flow-core.js in the plugin): a curve is
 * four cubic-bezier numbers or a spring with a bounce, paced to come to
 * rest right at the second keyframe.
 */
(() => {
  if (window.flowPage) {
    window.flowPage.init(document);
    return;
  }

  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  // ---------------------------------------------------------------------
  // Easing

  const bezier = (x1, y1, x2, y2) => {
    const at = (t, a, b) => 3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let low = 0;
      let high = 1;
      for (let i = 0; i < 40; i += 1) {
        const middle = (low + high) / 2;
        if (at(middle, x1, x2) < x) low = middle;
        else high = middle;
      }
      return at((low + high) / 2, y1, y2);
    };
  };

  const SPRING_SETTLE = 0.002;

  const springOffset = (zeta, t) => {
    const squared = 1 - zeta * zeta;
    if (squared <= 1e-12) return Math.exp(-t) * (1 + t);
    const damped = Math.sqrt(squared);
    return Math.exp(-zeta * t) * (Math.cos(damped * t) + (zeta / damped) * Math.sin(damped * t));
  };

  const SPRING_SETTLE_PHASE = (() => {
    let low = 0;
    let high = 64;
    for (let i = 0; i < 60; i += 1) {
      const middle = (low + high) / 2;
      if (Math.exp(-middle) * (1 + middle) > SPRING_SETTLE) low = middle;
      else high = middle;
    }
    return high;
  })();

  const spring = (bounce) => {
    const zeta = 1 - bounce;
    const damped = Math.sqrt(Math.max(0, 1 - zeta * zeta));
    let settle = SPRING_SETTLE_PHASE / zeta;
    if (damped > 0) settle = Math.min(settle, Math.log(1 / (SPRING_SETTLE * damped)) / zeta);
    const tail = springOffset(zeta, settle);
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      return 1 - springOffset(zeta, x * settle) + x * tail;
    };
  };

  // "0.33,0,0.67,1" or "spring,0.3"
  const parseCurve = (text) => {
    const parts = String(text).split(',').map((part) => part.trim());
    if (parts[0] === 'spring') return { spring: true, bounce: parseFloat(parts[1]) || 0 };
    const numbers = parts.map(Number);
    if (numbers.length !== 4 || numbers.some(Number.isNaN)) return null;
    return { spring: false, points: numbers };
  };

  const easing = (curve) => (curve.spring ? spring(curve.bounce) : bezier(...curve.points));

  const round = (value) => String(Math.round(value * 100) / 100);

  const describe = (curve) => (curve.spring
    ? `spring(bounce: ${round(curve.bounce)})`
    : `cubic-bezier(${curve.points.map(round).join(', ')})`);

  // a sampled path through box [left, bottom, right, top]
  const samplePath = (ease, [left, bottom, right, top], steps = 64) => {
    let d = '';
    for (let i = 0; i <= steps; i += 1) {
      const x = i / steps;
      const px = left + (right - left) * x;
      const py = bottom + (top - bottom) * ease(x);
      d += `${i ? 'L' : 'M'}${px.toFixed(2)} ${py.toFixed(2)}`;
    }
    return d;
  };

  // ---------------------------------------------------------------------
  // Scenes that replay

  const replay = (scene) => {
    if (still.matches || !scene.hasAttribute('data-played') || scene.flowBusy) return;
    scene.flowBusy = true;
    scene.removeAttribute('data-played');
    scene.setAttribute('data-replay', '');
    // eslint-disable-next-line no-unused-expressions
    scene.offsetWidth;
    scene.setAttribute('data-played', '');
    window.setTimeout(() => {
      scene.flowBusy = false;
    }, 1200);
  };

  const initReplay = (scene) => {
    if (scene.flowReady) return;
    scene.flowReady = true;
    scene.addEventListener('click', () => replay(scene));
    scene.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') replay(scene);
    });
  };

  // Scenes that loop while they're on screen: [data-inview] while any of
  // it shows, so CSS can pause the loop off screen.
  const initLoop = (scene) => {
    if (scene.flowReady) return;
    scene.flowReady = true;
    if (!('IntersectionObserver' in window)) {
      scene.setAttribute('data-inview', '');
      return;
    }
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => scene.toggleAttribute('data-inview', entry.isIntersecting));
    }).observe(scene);
  };

  // ---------------------------------------------------------------------
  // Auto Mode in Effect Controls (sections/flow-auto.liquid)
  //
  // Three rounds, each its own pair of keyframes and curve: the playhead
  // sets keyframe A, runs on and sets keyframe B, and Auto Mode eases them
  // right away - the straight line gives way to the curve, EaseFlow's
  // keyframes pop in from A to B, the EaseFlow · Auto badge lights up, the
  // toast names the curve. The timecode runs with the playhead.

  const AUTO_ROUNDS = [
    { name: 'Easy Ease', ease: bezier(0.33, 0, 0.67, 1), a: [16, 0.1], b: [80, 0.9] },
    { name: 'Smooth', ease: bezier(0.4, 0, 0.2, 1), a: [18, 0.9], b: [80, 0.1] },
    { name: 'Glide', ease: bezier(0.2, 0, 0, 1), a: [14, 0.1], b: [78, 0.9] },
  ];
  // the lane spans 0 to 2:15 (75 frames at 30 fps)
  const AUTO_FRAMES = 75;
  const AUTO_START = 6;
  const AUTO_ROUND_MS = 5400;

  const timecode = (frames) => {
    const f = Math.max(0, Math.round(frames));
    const pad = (n) => String(n).padStart(2, '0');
    return `00:00:${pad(Math.floor(f / 30))}:${pad(f % 30)}`;
  };

  // y of a value (0 at the bottom of the graph, 1 at the top) inside the
  // lane: the graph is inset 14px top and bottom
  const laneTop = (value) => `calc(14px + (100% - 28px) * ${(1 - value).toFixed(4)})`;

  const initAuto = (stage) => {
    if (stage.flowAutoReady || still.matches || typeof stage.animate !== 'function') return;
    stage.flowAutoReady = true;
    const part = (name) => stage.querySelector(`[data-fa="${name}"]`);
    const playhead = part('playhead');
    const keyA = part('a');
    const keyB = part('b');
    const linear = part('linear');
    const curve = part('curve');
    const dots = [...stage.querySelectorAll('[data-fa="dot"]')];
    const badge = part('badge');
    const toast = part('toast');
    const toastText = part('toast-text');
    const tc = part('tc');
    const valueText = part('value');
    if (!playhead || !keyA || !keyB || !curve) return;
    stage.setAttribute('data-fa-live', '');

    let run = 0;
    let running = false;
    let frame = null;
    let timers = [];
    let badgeGlow = null;

    const later = (ms, fn) => timers.push(window.setTimeout(fn, ms));
    const fade = (node, to, ms = 300) => node && node.animate([{ opacity: to }], { duration: ms, fill: 'forwards', easing: 'ease' });
    const pop = (node) => {
      node.animate([{ opacity: 0, scale: 0.2 }, { opacity: 1, scale: 1 }], { duration: 420, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'forwards' });
    };
    const hide = (node) => node && node.animate([{ opacity: 0 }], { duration: 0, fill: 'forwards' });

    // Position's x: the graph's 0 to 1 is 240 to 1680 px
    const positionOf = (value) => 240 + value * 1440;
    let shownX = 960;
    const showValue = (x) => {
      shownX = x;
      if (valueText) valueText.textContent = `${x.toFixed(1)}\u00a0\u00a0540.0`;
    };

    // the value being set, the way dragging it in Effect Controls looks
    const setValue = (to, ms, token) =>
      new Promise((resolve) => {
        const from = shownX;
        const ease = bezier(0.3, 0, 0.2, 1);
        const started = performance.now();
        if (valueText) valueText.setAttribute('data-editing', '');
        const step = (now) => {
          if (token !== run) return;
          const t = Math.min(1, (now - started) / ms);
          showValue(from + (to - from) * ease(t));
          if (t < 1) requestAnimationFrame(step);
          else {
            if (valueText) valueText.removeAttribute('data-editing');
            resolve();
          }
        };
        requestAnimationFrame(step);
      });

    const placePlayhead = (x) => {
      playhead.style.left = `${x}%`;
      const text = timecode((x / 100) * AUTO_FRAMES);
      if (tc && tc.textContent !== text) tc.textContent = text;
    };

    // the playhead glides from one spot to another
    const movePlayhead = (from, to, ms, token) =>
      new Promise((resolve) => {
        const ease = bezier(0.4, 0, 0.2, 1);
        const started = performance.now();
        const step = (now) => {
          if (token !== run) return;
          const t = Math.min(1, (now - started) / ms);
          placePlayhead(from + (to - from) * ease(t));
          if (t < 1) frame = requestAnimationFrame(step);
          else resolve();
        };
        frame = requestAnimationFrame(step);
      });

    const setRound = (round) => {
      const [ax, av] = round.a;
      const [bx, bv] = round.b;
      keyA.style.left = `${ax}%`;
      keyA.style.top = laneTop(av);
      keyB.style.left = `${bx}%`;
      keyB.style.top = laneTop(bv);
      const point = (x, v) => `${x.toFixed(2)} ${((1 - v) * 100).toFixed(2)}`;
      linear.setAttribute('d', `M${point(ax, av)} L${point(bx, bv)}`);
      let d = '';
      for (let i = 0; i <= 64; i += 1) {
        const t = i / 64;
        d += `${i ? 'L' : 'M'}${point(ax + (bx - ax) * t, av + (bv - av) * round.ease(t))}`;
      }
      curve.setAttribute('d', d);
      dots.forEach((dot, i) => {
        const t = (i + 1) / (dots.length + 1);
        dot.style.left = `${(ax + (bx - ax) * t).toFixed(2)}%`;
        dot.style.top = laneTop(av + (bv - av) * round.ease(t));
      });
      if (toastText) toastText.textContent = (stage.dataset.faEased || 'Eased with [name]').replace('[name]', () => round.name);
    };

    const reset = () => {
      // drop the last round's animations (not the CSS entrance)
      stage.getAnimations({ subtree: true }).forEach((animation) => {
        if (!(typeof CSSAnimation === 'function' && animation instanceof CSSAnimation)) animation.cancel();
      });
      badgeGlow = null;
      [keyA, keyB, linear, curve, toast, ...dots].forEach(hide);
      if (valueText) valueText.removeAttribute('data-editing');
    };

    const glow = () => {
      if (badgeGlow) badgeGlow.cancel();
      badgeGlow = badge.animate(
        [
          {},
          {
            offset: 0.25,
            background: 'rgb(41 151 255 / 0.9)',
            boxShadow: 'inset 0 0 0 1px rgb(150 205 255), 0 0 0 5px rgb(41 151 255 / 0.22), 0 0 44px rgb(41 151 255 / 0.95)',
            color: '#fff',
            scale: 1.08,
          },
          {
            offset: 0.55,
            background: 'rgb(41 151 255 / 0.9)',
            boxShadow: 'inset 0 0 0 1px rgb(150 205 255), 0 0 0 5px rgb(41 151 255 / 0.22), 0 0 44px rgb(41 151 255 / 0.95)',
            color: '#fff',
            scale: 1.08,
          },
          {
            background: 'rgb(41 151 255 / 0.26)',
            boxShadow: 'inset 0 0 0 1px rgb(41 151 255 / 0.8), 0 0 20px rgb(41 151 255 / 0.4)',
            color: '#fff',
          },
        ],
        { duration: 1600, easing: 'cubic-bezier(0.3, 0, 0.2, 1)', fill: 'forwards' }
      );
    };

    const playRound = async (index, token) => {
      const round = AUTO_ROUNDS[index];
      reset();
      setRound(round);
      placePlayhead(AUTO_START);
      await movePlayhead(AUTO_START, round.a[0], 520, token);
      if (token !== run) return;
      // set the value, and the stopwatch makes keyframe A
      await setValue(positionOf(round.a[1]), 380, token);
      if (token !== run) return;
      pop(keyA);
      await new Promise((resolve) => later(200, resolve));
      if (token !== run) return;
      await movePlayhead(round.a[0], round.b[0], 1050, token);
      if (token !== run) return;
      // a new value where the playhead stopped makes keyframe B, and Auto
      // takes it
      await setValue(positionOf(round.b[1]), 420, token);
      if (token !== run) return;
      pop(keyB);
      fade(linear, 1, 150);
      later(300, () => {
        fade(linear, 0, 450);
        fade(curve, 1, 500);
        dots.forEach((dot, i) => later(60 + i * 55, () => pop(dot)));
      });
      later(1050, glow);
      later(1400, () => {
        toast.animate(
          [
            { opacity: 0, transform: 'translateY(14px) scale(0.96)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 600, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
        );
      });
    };

    const play = () => {
      if (running) return;
      running = true;
      run += 1;
      const token = run;
      timers.forEach((id) => window.clearTimeout(id));
      timers = [];
      if (frame) cancelAnimationFrame(frame);
      AUTO_ROUNDS.forEach((_, index) => {
        later(index * AUTO_ROUND_MS, () => {
          if (token === run) playRound(index, token);
        });
      });
      later(AUTO_ROUNDS.length * AUTO_ROUND_MS - 1800, () => {
        if (token === run) running = false;
      });
    };

    // the first round starts once the panel has risen into view
    reset();
    placePlayhead(AUTO_START);
    const start = () => window.setTimeout(play, 900);
    if (stage.hasAttribute('data-played')) start();
    else {
      const watch = new MutationObserver(() => {
        if (!stage.hasAttribute('data-played')) return;
        watch.disconnect();
        start();
      });
      watch.observe(stage, { attributes: true, attributeFilter: ['data-played'] });
    }
    stage.addEventListener('click', play);
    stage.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') play();
    });
  };

  // ---------------------------------------------------------------------
  // The playground

  // the graph's box in its viewBox: x 40-280, the move from y 170 to 70
  const GRAPH = { left: 40, right: 280, bottom: 170, top: 70 };
  const gx = (x) => GRAPH.left + (GRAPH.right - GRAPH.left) * x;
  const gy = (y) => GRAPH.bottom + (GRAPH.top - GRAPH.bottom) * y;

  const initPlayground = (root) => {
    if (root.flowReady) return;
    root.flowReady = true;

    const graph = root.querySelector('[data-flow-graph]');
    const svg = graph.querySelector('svg');
    const path = root.querySelector('[data-flow-curve]');
    const arms = [...root.querySelectorAll('[data-flow-arm]')];
    const knobs = [...root.querySelectorAll('[data-flow-knob]')];
    const hits = [...root.querySelectorAll('[data-flow-handle]')];
    const strip = root.querySelector('[data-flow-strip]');
    const ball = root.querySelector('[data-flow-ball]');
    const ring = root.querySelector('[data-flow-ring]');
    const nameOut = root.querySelector('[data-flow-name]');
    const codeOut = root.querySelector('[data-flow-code]');
    const tiles = [...root.querySelectorAll('[data-flow-preset]')];
    const tabs = [...root.querySelectorAll('[data-flow-tab]')];

    let curve = parseCurve('0.33,0,0.67,1');
    let ease = easing(curve);
    let shown = curve;
    let frame = 0;

    const draw = (c) => {
      if (c.spring) {
        path.setAttribute('d', samplePath(spring(c.bounce), [GRAPH.left, GRAPH.bottom, GRAPH.right, GRAPH.top], 120));
        graph.setAttribute('data-spring', '');
        return;
      }
      graph.removeAttribute('data-spring');
      const [x1, y1, x2, y2] = c.points;
      path.setAttribute('d', `M${gx(0)} ${gy(0)}C${gx(x1)} ${gy(y1)} ${gx(x2)} ${gy(y2)} ${gx(1)} ${gy(1)}`);
      [[x1, y1], [x2, y2]].forEach(([x, y], i) => {
        arms[i].setAttribute('x2', gx(x));
        arms[i].setAttribute('y2', gy(y));
        knobs[i].setAttribute('cx', gx(x));
        knobs[i].setAttribute('cy', gy(y));
        hits[i].setAttribute('cx', gx(x));
        hits[i].setAttribute('cy', gy(y));
      });
    };

    // a bezier glides into the next one; anything with a spring just swaps
    const morph = (to) => {
      cancelAnimationFrame(frame);
      const from = shown;
      if (still.matches || from.spring || to.spring) {
        shown = to;
        draw(to);
        return;
      }
      const start = performance.now();
      const glide = bezier(0.2, 0, 0, 1);
      const step = (now) => {
        const t = Math.min((now - start) / 420, 1);
        const k = glide(t);
        shown = { spring: false, points: from.points.map((value, i) => value + (to.points[i] - value) * k) };
        draw(shown);
        if (t < 1) frame = requestAnimationFrame(step);
        else shown = to;
      };
      frame = requestAnimationFrame(step);
    };

    // ---- the motion strip: the dot eases, the ring goes at linear speed
    let run = 0;
    const place = (x) => {
      const width = strip.querySelector('span').clientWidth;
      ball.style.transform = `translateX(${(ease(x) * width).toFixed(2)}px)`;
      ring.style.transform = `translateX(${(x * width).toFixed(2)}px)`;
    };

    const play = (rounds = 2) => {
      cancelAnimationFrame(run);
      if (still.matches) {
        place(1);
        return;
      }
      const move = 1100;
      const pause = 450;
      const start = performance.now() + 120;
      const step = (now) => {
        const elapsed = Math.max(0, now - start);
        const round = Math.floor(elapsed / (move + pause));
        if (round >= rounds) {
          place(1);
          return;
        }
        place(Math.min((elapsed - round * (move + pause)) / move, 1));
        run = requestAnimationFrame(step);
      };
      run = requestAnimationFrame(step);
    };

    const show = (next, name) => {
      curve = next;
      ease = easing(curve);
      nameOut.textContent = name;
      codeOut.textContent = describe(curve);
    };

    const select = (tile) => {
      const next = parseCurve(tile.dataset.flowPreset);
      if (!next) return;
      tiles.forEach((other) => other.setAttribute('aria-pressed', String(other === tile)));
      show(next, tile.dataset.flowPresetName);
      morph(next);
      play();
    };

    tiles.forEach((tile) => tile.addEventListener('click', () => select(tile)));
    strip.addEventListener('click', () => play(1));

    // ---- tabs
    const openTab = (tab, focus) => {
      tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => openTab(tab));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        openTab(tabs[(index + keys[event.key] + tabs.length) % tabs.length], true);
      });
    });

    // ---- dragging a handle: x stays within the move, y may overshoot
    const toGraph = (event) => {
      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const local = point.matrixTransform(svg.getScreenCTM().inverse());
      return [(local.x - GRAPH.left) / (GRAPH.right - GRAPH.left), (local.y - GRAPH.bottom) / (GRAPH.top - GRAPH.bottom)];
    };

    hits.forEach((hit, index) => {
      hit.addEventListener('pointerdown', (event) => {
        if (curve.spring) return;
        event.preventDefault();
        cancelAnimationFrame(frame);
        hit.setPointerCapture(event.pointerId);
        graph.setAttribute('data-dragging', '');
        knobs[index].setAttribute('r', 10);
        const points = shown.points.slice();
        const moveTo = (moveEvent) => {
          let [x, y] = toGraph(moveEvent);
          x = Math.min(1, Math.max(0, x));
          y = Math.min(1.6, Math.max(-0.6, y));
          if (moveEvent.shiftKey) {
            x = Math.round(x * 20) / 20;
            y = Math.round(y * 20) / 20;
          }
          points[index * 2] = Math.round(x * 100) / 100;
          points[index * 2 + 1] = Math.round(y * 100) / 100;
          shown = { spring: false, points: points.slice() };
          draw(shown);
          show(shown, root.dataset.customName || 'Your curve');
          tiles.forEach((tile) => tile.setAttribute('aria-pressed', 'false'));
          place(1);
        };
        const end = () => {
          hit.removeEventListener('pointermove', moveTo);
          hit.removeEventListener('pointerup', end);
          hit.removeEventListener('pointercancel', end);
          graph.removeAttribute('data-dragging');
          knobs[index].setAttribute('r', 8);
          play(1);
        };
        hit.addEventListener('pointermove', moveTo);
        hit.addEventListener('pointerup', end);
        hit.addEventListener('pointercancel', end);
      });
    });

    // rest at the end; play once when the playground comes into view
    draw(curve);
    place(1);
    window.addEventListener('resize', () => place(1), { passive: true });
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        seen.disconnect();
        play(2);
      }, { threshold: 0.4 });
      seen.observe(strip);
    }
  };

  // ---------------------------------------------------------------------
  // Picking a version in the buy box

  const initChoose = (link) => {
    if (link.flowReady) return;
    link.flowReady = true;
    link.addEventListener('click', (event) => {
      const value = link.dataset.flowChoose;
      const input = [...document.querySelectorAll('variant-picker input[type="radio"]')]
        .find((candidate) => candidate.value === value && !candidate.closest('.product-sticky-form, [data-sticky]'));
      const select = !input && [...document.querySelectorAll('variant-picker select')]
        .find((candidate) => [...candidate.options].some((option) => option.value === value));
      if (!input && !select) return;
      event.preventDefault();
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const picker = (input || select).closest('variant-picker');
      const top = picker.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.22;
      window.scrollTo({ top: Math.max(0, top), behavior: still.matches ? 'auto' : 'smooth' });
    });
  };

  // spring thumbnails (the playground's tiles, the library cards)
  const drawMini = (mini) => {
    const box = (mini.dataset.flowMiniBox || '4 36 56 8').split(' ').map(Number);
    mini.setAttribute('d', samplePath(spring(parseFloat(mini.dataset.flowMini) || 0), box, 40));
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-flow-mini]').forEach(drawMini);
    scope.querySelectorAll('[data-flow-replay]').forEach(initReplay);
    scope.querySelectorAll('[data-flow-loop]').forEach(initLoop);
    scope.querySelectorAll('[data-flow-auto]').forEach(initAuto);
    scope.querySelectorAll('[data-flow-playground]').forEach(initPlayground);
    scope.querySelectorAll('[data-flow-choose]').forEach(initChoose);
  };

  window.flowPage = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

